import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
  type FilterField,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { promoItemsQueryOptions } from '@/features/promotion/api/promotion.api';
import { regionalSalesManagersQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getRegionalSalesManagerFilterField } from '@/utils/filterFields';
import { getUpcomingSearch } from '@/utils/upcomingSearch';
import { donationBudgetsInfiniteQueryOptions } from '../api/marketPromotionP2.api';
import { DonationBudgetFormDialog } from '../components/DonationBudgetFormDialog';
import { getDonationBudgetColumns } from '../components/MpP2Columns';
import { DONATION_ITEM_LOOKUP_PARAMS } from '../constants/marketPromotion.constants';
import { useDonationBudgetFilters } from '../hooks/useDonationBudgetFilters';
import type { DonationBudget } from '../types/marketPromotionP2.types';
import { getBudgetStatCards } from '../utils/budgetStatCards';
import { getDonationItemFilterField } from '../utils/mpFilterFields';

export function DonationBudgetPage() {
  const controller = useDonationBudgetFilters();
  const { search } = controller;

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editing, setEditing] = useState<DonationBudget | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);

  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(donationBudgetsInfiniteQueryOptions(search));
  const { data: items } = useQuery(promoItemsQueryOptions(DONATION_ITEM_LOOKUP_PARAMS));
  const { data: regionalSalesManagers } = useQuery(regionalSalesManagersQueryOptions());

  const filterFields: FilterField[] = [
    getRegionalSalesManagerFilterField(regionalSalesManagers),
    getDonationItemFilterField(items?.data),
  ];

  const columns = getDonationBudgetColumns({
    onEdit: (row) => {
      setEditing(row);
      setFormOpen(true);
    },
  });

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Market Promotion Budget Entry"
        search={getUpcomingSearch('RSM, Donation Item')}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
        createAction={{
          label: 'Add Budget',
          onClick: () => {
            setEditing(null);
            setFormOpen(true);
          },
        }}
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel
          fields={filterFields}
          values={controller.filterValues}
          defaultValues={controller.defaultFilterValues}
          onChange={controller.handleFilterChange}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the budgets"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid isLoading={isLoading} stats={getBudgetStatCards(firstPageMeta?.totals)} />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="member"
            emptyMessage="No budget matches"
            {...loadMoreProps}
          />
        </>
      )}

      <DonationBudgetFormDialog
        key={`${editing?.id ?? 'new'}:${String(isFormOpen)}`}
        budget={editing}
        open={isFormOpen}
        onOpenChange={setFormOpen}
      />
    </div>
  );
}
